/**
 * App-owned theme state.
 *
 * The DS `ThemeProvider` is CONTROLLED-ONLY — it sets `data-theme` on its own
 * `norican-root` div but ships no hook, no OS-preference read and no persistence.
 * So the app owns the theme: this provider seeds from `localStorage` (else the OS
 * preference), persists the choice, mirrors it onto `document.documentElement`
 * (the page/body background resolves from `<html data-theme>`, which sits OUTSIDE
 * the DS provider div), and passes it into the DS `ThemeProvider` so portaled
 * menus/dialogs inherit the scope. `useAppTheme()` exposes the setter to the shell.
 */

import { type Theme, ThemeProvider as DsThemeProvider } from "@norican/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { logger } from "@/lib/logger";

const STORAGE_KEY = "rfq-theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch (error) {
    logger.debug("[theme] localStorage read failed", error);
  }
  try {
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch (error) {
    logger.debug("[theme] matchMedia unavailable", error);
  }
  return "light";
}

export interface AppThemeProviderProps {
  children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (error) {
      logger.debug("[theme] localStorage write failed", error);
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>
      <DsThemeProvider theme={theme} density="comfortable">
        {children}
      </DsThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useAppTheme must be used within <AppThemeProvider>");
  }
  return context;
}
